<template>
    <Progressbar v-if="loading" />
    <NetworkError v-else-if="errors" @tryAgain="getData" />
    <div class="flex flex-row gap-6 w-full h-full" v-else>
        <div
            class="flex flex-1 flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-base-300 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-base-200 px-2 sm:px-3 lg:px-6">
            <div class="alert bg-orange-custom-3 text-orange-custom-4">
                <span class="mx-auto"> {{ __('Final registration or editing of the invoice') }}</span>
            </div>
            <NotFound v-if="!products.length" />
            <div class="card cart mt-4 bg-white !overflow-visible" v-else>
                <div class="card-body p-0">
                    <ProductItem :products="products" :loading="loading" @changeQuantity="changeQuantity" :meta="meta"
                        @setProduct="setProduct" />
                </div>
            </div>
            <div v-if="products.length && cart.check_date" class="mt-3">
                <div class="alert bg-orange-custom-7 py-1 justify-start mt-2 flex-row">
                    <ExclamationTriangleIcon class="w-5 h-5 text-orange-custom-8" />
                    <span class="text-orange-custom-8 text-xs md:text-base font-semibold">
                        {{ __('Please carefully read and confirm the settlement term table for Czech goods') }}
                    </span>
                </div>
                <div class="overflow-x-auto mt-2">
                    <table class="table w-full table-compact md:table-normal">
                        <thead class="border-b">
                            <tr>
                                <th class="!text-white !bg-green-custom-6">{{ __('Number of goods') }}</th>
                                <th class="!text-white !bg-green-custom-6">{{ __('settlement semester') }}</th>
                                <th class="!text-white !bg-green-custom-6">{{ __('settlement date') }}</th>
                                <th class="!text-white !bg-green-custom-6">{{ __('Amount') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="check in cart.checks" class="bg-white even:bg-green-custom-5">
                                <td class="text-black text-center">{{ check.product_count }}</td>
                                <td class="text-black text-center">{{ check.term + ' ' + __('Day') }}</td>
                                <td class="text-black text-center">{{ check.date }}</td>
                                <td class="text-black text-center">
                                    <span class="text-black">{{ check.amount.toLocaleString('en-Us') }}</span>
                                    <Currency />
                                </td>
                            </tr>
                            <tr class="bg-red-custom-3">
                                <td colspan="2" class="text-red-custom-2">{{ __('Clearing date on a check') }}</td>
                                <td colspan="2" class="text-red-custom-2">{{ cart.check_date }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="flex flex-col xl:flex-row gap-3 w-full items-end xl:items-start justify-between mt-3" v-if="products.length">
                <div class="flex flex-col gap-2 xl:gap-3 order-last w-full xl:order-first">
                    <div class="form-control px-6 bg-slate-300 rounded-md" v-if="cart.check_date" :class="{'animate-pulse': !cart.check_confirm}">
                        <label class="label cursor-pointer justify-start" v-if="cart.check_text">
                            <input type="checkbox" v-model="cart.check_confirm"
                                class="checkbox checkbox-sm checkbox-error checked:checkbox-error" />
                            <span class="label-text text-red-custom-4 font-bold mr-3 text-xs md:text-base">
                                {{ cart.check_text }}
                            </span>
                        </label>
                    </div>
                    <div class="form-control px-6 bg-gray-custom-9 rounded-md" v-if="products.length" :class="{'animate-pulse': !cart.confirm}">
                        <label class="label cursor-pointer justify-start">
                            <input type="checkbox" v-model="cart.confirm"
                                class="checkbox checkbox-warning checked:checkbox-warning checkbox-sm" />
                            <span class="label-text text-yellow-custom-4 font-bold mr-3 text-xs md:text-base">
                                {{ cart.confirm_text }}
                            </span>
                        </label>
                    </div>
                </div>
                <div class="card w-full max-w-sm 2xl:max-w-md rounded-lg">
                    <div class="card-body p-0 gap-1">
                        <div class="bg-blue-custom-11 flex justify-between text-blue-custom-12 p-2">
                            <span class="text-sm md:text-base">{{ __('Cash purchase total') }}</span>
                            <span class="text-sm md:text-base">
                                {{ (cart.cash + (cart.check_condition ? 0 : cart.check))?.toLocaleString() }}
                                <Currency />
                            </span>
                        </div>
                        <div v-if="cart.check_condition"
                            class="bg-green-custom-2 flex justify-between text-green-custom-6 p-2">
                            <span class="text-sm md:text-base">{{ __('Check purchase total') }}</span>
                            <span class="text-sm md:text-base">
                                {{ cart.check?.toLocaleString() }}
                                <Currency />
                            </span>
                        </div>
                        <div class="bg-red-custom-5 flex justify-between text-red-custom-2 p-2">
                            <span class="text-sm md:text-base">{{ __('Total discounts') }}</span>
                            <span class="text-sm md:text-base">
                                {{ cart.discount?.toLocaleString() }}
                                <Currency />
                            </span>
                        </div>
                        <div v-if="cart.tax > 0"
                            class="bg-blue-custom-11 flex justify-between text-blue-custom-12 p-2">
                            <span class="text-sm md:text-base">{{ __('Tax') }}</span>
                            <span class="text-sm md:text-base">
                                {{ cart.tax?.toLocaleString() }}
                                <Currency />
                            </span>
                        </div>
                        <div
                            class="bg-green-custom-5 flex justify-between text-green-custom-6 p-2">
                            <span class="text-sm md:text-base">{{ __('Payable Amount') }}</span>
                            <span class="text-sm md:text-base">
                                {{ (cart.total + cart.tax)?.toLocaleString() }}
                                <Currency />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex justify-end mt-4 pb-4" v-if="products.length">
                        <router-link :to="{ name: 'product' }" class="btn btn-info btn-sm">{{ __('Products page')
                            }}</router-link>
                        <button class="btn btn-success btn-sm mr-3" @click="checkOrder">
                            {{ __('Registration and final approval') }}
                        </button>
                    </div>
        </div>
    </div>
    <ReserveModal :reserve="reserve" @change="changeQuantity" />
    <ProductQuantityModal />
    <UnavailableProductModal :show="cart.changed && cart.deleted" @refreshCart="refreshCart" />
    <Teleport to="#app">
        <div class="modal" :class="{ 'modal-open': errorModal.error }">
            <div class="modal-box bg-red-custom-3">
                <ExclamationTriangleIcon class="w-24 h-24 mx-auto text-red-custom-4" />
                <p class="text-red-custom-4 pt-4">{{ errorModal.message }}</p>
                <div class="modal-action">
                    <label class="btn btn-success btn-sm" @click="registerOrder(true)"
                        v-if="errorModal.accept || false">{{
                            __('Record') }}</label>
                    <label class="btn btn-success btn-sm" @click="refreshCart('reserve')"
                        v-if="errorModal.deleted || false">{{ __('I make a reservation') }}</label>
                    <label class="btn btn-primary btn-sm" v-if="errorModal.changed || false"
                        @click="refreshCart('delete')">{{ __('Update shopping cart') }}</label>
                    <label class="btn btn-error btn-sm" @click="errorModal.error = false" v-else>{{ __('Close')
                        }}</label>
                </div>
            </div>
        </div>
    </Teleport>
    <Teleport to="#app">
        <input type="checkbox" id="order-modal" class="modal-toggle" />
        <div class="modal">
            <div class="modal-box">
                <h3 class="font-bold text-lg">{{ __('Final approval') }}</h3>
                <p class="pt-4 text-sm">{{ __('If you have an explanation for this order, enter it in the box below') }}
                </p>
                <p class="text-sm text-error font-semibold" v-if="cart.discount">{{ __('Dear user, you have discounted products in your invoice and these products will be settled in cash') }}</p>
                <textarea
                    class="textarea mt-2 w-full bg-blue-custom-1 text-gray-custom-1 placeholder-gray-custom-1 resize-none outline-none"
                    v-model="cart.description" :placeholder="__('Customer description (optional)')"></textarea>
                <div class="modal-action mt-3">
                    <label class="btn btn-success btn-sm" @click="registerOrder(false)" for="order-modal">{{
                        __('Submit') }}</label>
                    <label class="btn btn-error btn-sm" for="order-modal">{{ __('Refuse') }}</label>
                </div>
            </div>
        </div>
    </Teleport>
    <Teleport to="#app">
        <input type="checkbox" id="delete-modal" class="modal-toggle" />
        <div class="modal">
            <div class="modal-box">
                <h3 class="font-bold text-lg">{{ __('Delete') }}</h3>
                <p class="pt-4">{{ __('Are you sure you want to delete this product?') }}</p>
                <div class="modal-action">
                    <label class="btn btn-success btn-sm px-4" @click="removeCart(product, true)" for="delete-modal">{{
                        __('Yes') }}</label>
                    <label class="btn btn-error btn-sm" for="delete-modal">{{ __('No') }}</label>
                </div>
            </div>
        </div>
    </Teleport>
</template>
<script setup>
import useProducts from "../../../composables/shop/product";
import { computed, onMounted, reactive, ref } from 'vue';
import {
    ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import Currency from '../../svg/Currency.vue';
import useOrders from "../../../composables/shop/order";
import ReserveModal from "../modal/ReserveModal.vue";
import UnavailableProductModal from "../modal/UnavailableProductModal.vue";
import ProductItem from "./ProductItem.vue";
import ProductQuantityModal from "../modal/ProductQuantityModal.vue";

const { loading, product, products, getCart, removeCart, changeQuantity, meta, toast, cart, reserve, errors } = useProducts();
const { storeOrder, errorModal } = useOrders();
const setProduct = (id) => product.id = id;
const registerOrder = (force) => storeOrder(false, '', cart.description, force);
const checkOrder = () => {
    if (!cart.confirm) toast.error(window.i18n['Please confirm the box above']);
    else if (cart.check_date && cart.check_text && !cart.check_confirm) toast.error(window.i18n['Confirm the settlement term first']);
    else document.getElementById('order-modal').checked = true;
}
const refreshCart = (type) => {
    errorModal.value = '';
    getCart(type);
}
const getData = () => getCart();
onMounted(() => getData());
</script>